/**
 * Structured diagnostics for critical data flows.
 * Logs in development; minimal in production.
 */

import { getDebugFlags } from "./debugFlags";
import { classifySupabaseError, type ClassifiedError } from "./errorClassifier";

export interface FlowDiagnosticContext {
  flow: string;
  operation: "insert" | "update" | "delete" | "select";
  entity: string;
  userId?: string;
  entityId?: string;
  durationMs?: number;
  success: boolean;
  error?: unknown;
  classified?: ClassifiedError;
  fetchAfterWrite?: "ok" | "failed" | "skipped" | "not_applicable";
  optimisticUsed?: boolean;
}

export function logFlowDiagnostic(ctx: FlowDiagnosticContext): void {
  const flags = getDebugFlags();
  if (!flags.flowDiagnostics && !flags.errorClassification) return;

  const payload: Record<string, unknown> = {
    flow: ctx.flow,
    operation: ctx.operation,
    entity: ctx.entity,
    success: ctx.success,
    ...(ctx.userId && { userId: ctx.userId }),
    ...(ctx.entityId && { entityId: ctx.entityId }),
    ...(ctx.durationMs != null && { durationMs: ctx.durationMs }),
    ...(ctx.fetchAfterWrite && { fetchAfterWrite: ctx.fetchAfterWrite }),
    ...(ctx.optimisticUsed != null && { optimisticUsed: ctx.optimisticUsed }),
  };

  if (ctx.error) {
    const classified = ctx.classified ?? classifySupabaseError(ctx.error);
    payload.error = ctx.error;
    payload.classified = classified;
    payload.hint = classified.hint;
  }

  if (ctx.success) {
    console.debug("[StudyDo flow]", payload);
  } else {
    console.warn("[StudyDo flow]", payload);
  }
}

/** Wrap an async operation with timing and structured diagnostics */
export async function withFlowDiagnostics<T>(
  ctx: Omit<FlowDiagnosticContext, "success" | "durationMs" | "error" | "classified">,
  fn: () => Promise<T>
): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    logFlowDiagnostic({
      ...ctx,
      success: true,
      durationMs: Math.round(performance.now() - start),
    });
    return result;
  } catch (err) {
    const classified = classifySupabaseError(err);
    logFlowDiagnostic({
      ...ctx,
      success: false,
      durationMs: Math.round(performance.now() - start),
      error: err,
      classified,
    });
    throw err;
  }
}
